import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Heart, MessageCircle, Search, UserX, Crown } from "lucide-react";

export default function FriendsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("friends");
  const [friends, setFriends] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [friendsRes, followersRes, followingRes] = await Promise.all([
        api.get("/user-follows/my/friends"),
        api.get("/user-follows/my/followers"),
        api.get("/user-follows/my/following"),
      ]);
      setFriends(friendsRes.data.friends || []);
      setFollowers(followersRes.data.followers || []);
      setFollowing(followingRes.data.following || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleUnfollow = async (userId) => {
    try {
      await api.post(`/user-follows/${userId}/toggle`);
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const handleMessage = async (userId) => {
    try {
      const res = await api.post("/messages/conversations", { recipient_id: userId });
      navigate(`/messages/${res.data.conversation.id}`);
    } catch (err) { console.error(err); }
  };

  const getList = () => {
    const list = tab === "friends" ? friends : tab === "followers" ? followers : following;
    if (!search.trim()) return list;
    return list.filter((u) => u.pseudo?.toLowerCase().includes(search.toLowerCase()));
  };

  const currentList = getList();

  const UserRow = ({ u, showUnfollow }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-2xl p-4 flex items-center gap-3 hover:border-[#FF6B35]/30 transition-colors"
      data-testid={`friend-row-${u.id}`}
    >
      <Link to={`/user/${u.id}`} className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center shrink-0 overflow-hidden">
        {u.avatar_url ? (
          <img src={u.avatar_url} alt="" className="w-full h-full rounded-2xl object-cover" />
        ) : (
          <span className="text-white font-heading font-bold text-lg">{(u.pseudo || "?")[0].toUpperCase()}</span>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/user/${u.id}`} className="font-heading font-bold text-sm text-card-foreground hover:text-[#FF6B35] transition-colors truncate block">
          {u.pseudo}
        </Link>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="px-2 py-0.5 rounded-full bg-muted text-[9px] font-medium text-card-foreground/80 border border-border">
            {u.role === "artist" ? "Artiste" : "Fan"}
          </span>
          {tab === "friends" && (
            <span className="flex items-center gap-0.5 text-[9px] text-pink-500 font-medium">
              <Heart className="w-2.5 h-2.5" /> Ami
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={() => handleMessage(u.id)}
          className="w-9 h-9 rounded-xl bg-[#7000FF]/10 flex items-center justify-center text-[#7000FF] hover:bg-[#7000FF]/20 transition-colors"
          data-testid={`friend-msg-${u.id}`}
        >
          <MessageCircle className="w-4 h-4" />
        </button>
        {showUnfollow && (
          <button
            onClick={() => handleUnfollow(u.id)}
            className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center text-red-400 hover:bg-red-100 transition-colors"
            data-testid={`friend-unfollow-${u.id}`}
          >
            <UserX className="w-4 h-4" />
          </button>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="py-6 space-y-5" data-testid="friends-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-pink-100 flex items-center justify-center">
          <Heart className="w-5 h-5 text-pink-500" />
        </div>
        <div>
          <h1 className="font-heading font-extrabold text-xl text-foreground">Mes Amis</h1>
          <p className="text-xs text-muted-foreground">{friends.length} ami{friends.length > 1 ? "s" : ""} / {followers.length} follower{followers.length > 1 ? "s" : ""}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-popover rounded-2xl p-1.5" data-testid="friends-tabs">
        {[
          { key: "friends", label: `Amis (${friends.length})`, icon: Heart },
          { key: "followers", label: `Followers (${followers.length})`, icon: Users },
          { key: "following", label: `Suivis (${following.length})`, icon: Crown },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
              tab === t.key
                ? "bg-[#FF6B35] text-white shadow-lg shadow-[#FF6B35]/30"
                : "text-muted-foreground hover:text-[#AFA8BA]"
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher..."
          className="w-full bg-card border border-border rounded-full pl-10 pr-4 py-2.5 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
          data-testid="friends-search"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-foreground/5 rounded-2xl h-16 animate-pulse" />
          ))}
        </div>
      ) : currentList.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-14 h-14 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-muted-foreground font-medium">
            {tab === "friends" ? "Aucun ami pour le moment" :
             tab === "followers" ? "Aucun follower" : "Tu ne suis personne"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {tab === "friends"
              ? "Quand toi et un autre utilisateur vous suivez mutuellement, vous devenez amis !"
              : tab === "followers"
              ? "Quand quelqu'un te suit, il apparait ici"
              : "Explore et suis des fans et artistes !"}
          </p>
        </div>
      ) : (
        <AnimatePresence>
          <div className="space-y-3">
            {currentList.map((u) => (
              <UserRow key={u.id} u={u} showUnfollow={tab === "following" || tab === "friends"} />
            ))}
          </div>
        </AnimatePresence>
      )}
    </div>
  );
}
